"use client";

import { FileText, LockKeyhole } from "lucide-react";
import { type ContentKind, type ContentRecord } from "./content-model";
import type { AttachmentSummary } from "../../lib/api";

const isImage = (row: AttachmentSummary) =>
  row.contentType === "image/jpeg" || row.contentType === "image/png";
const fileSrc = (id: string) => `/api/v1/attachments/${encodeURIComponent(id)}/content`;

export function ContentPreview({
  kind,
  record,
  files
}: {
  kind: ContentKind;
  record: Partial<ContentRecord>;
  /** 稿件的附件行（门户稿件才有）；没读到时只按名称目录预览。 */
  files?: AttachmentSummary[] | null;
}) {
  const portal = kind === "portal";
  const body = Array.isArray(record.body) ? record.body : (record.body ?? "").split(/\n\s*\n/);
  // 与公开投影同一口径：封面由服务端给出；其余图片排在正文后；非图片文件进附件目录，名称项只补没有同名文件的。
  const active = (files ?? []).filter((row) => row.status === "ACTIVE");
  const cover = portal ? record.cover : undefined;
  const figures = active.filter((row) => isImage(row) && row.id !== cover?.id);
  const directory = portal
    ? [
        ...active.filter((row) => !isImage(row)).map((row) => row.fileName),
        ...(record.attachments ?? [])
          .map((row) => row.name)
          .filter((name) => !active.some((row) => row.fileName === name))
      ]
    : [];
  return (
    <section
      className={`content-preview ${portal ? "content-preview--portal" : "content-preview--notice"}`}
      aria-label={portal ? "门户阅读预览" : "会员通知预览"}
    >
      <div className="content-preview__label">
        {portal ? <FileText size={14} /> : <LockKeyhole size={14} />}
        <span>{portal ? "门户阅读预览" : "会员通知预览"}</span>
        <span>仅供核对</span>
      </div>
      <article className="content-paper">
        <p className="content-paper__category">
          {portal ? record.channelTitle || "待选栏目" : record.category || "通知"}
        </p>
        <h2>{record.title || "在这里写下标题"}</h2>
        <div className="content-paper__byline">
          {portal ? (
            <>
              <span>{record.displayDate || "展示日期待填写"}</span>
              {record.publicSource && <span>来源：{record.publicSource}</span>}
            </>
          ) : (
            <>
              <span>{record.publisherLabel || "发布署名待填写"}</span>
              <span>{record.requiresAcknowledgement ? "需确认知悉" : "无需确认知悉"}</span>
            </>
          )}
        </div>
        {portal && record.digest && <p className="content-paper__digest">{record.digest}</p>}
        {cover && (
          <figure className="content-paper__figure content-paper__figure--cover">
            {/* 工作台内部下载口，带会话；公开后公众看到的是同一字节经净化器后的版本。 */}
            <img src={fileSrc(cover.id)} alt={cover.name} decoding="async" />
            <figcaption>封面 · {cover.name}</figcaption>
          </figure>
        )}
        <div className="content-paper__body">
          {body.some((p) => p.trim()) ? (
            body.map((p, index) => <p key={index}>{p}</p>)
          ) : (
            <p className="content-paper__placeholder">正文将在这里呈现。空行用于分段。</p>
          )}
        </div>
        {figures.map((row) => (
          <figure key={row.id} className="content-paper__figure content-paper__figure--after">
            <img src={fileSrc(row.id)} alt={row.fileName} loading="lazy" decoding="async" />
            <figcaption>插图 · {row.fileName}</figcaption>
          </figure>
        ))}
        {portal && !!record.themes?.length && (
          <div className="content-paper__themes">
            {record.themes.map((theme, index) => (
              <span key={index}>{theme}</span>
            ))}
          </div>
        )}
        {directory.length > 0 && (
          <div className="content-paper__attachments">
            <h3>附件目录</h3>
            {directory.map((name, index) => (
              <p key={index}>{name}</p>
            ))}
          </div>
        )}
      </article>
      <p className="content-preview__footnote">
        {portal
          ? "预览不生成公开链接；是否公开以审查和放行结果为准。"
          : "此处核对正文与署名，接收范围请在送审前单独确认。"}
      </p>
    </section>
  );
}
